// Bilangan rasional eksak berbasis BigInt. Tidak pernah memakai float untuk kepemilikan.

export interface Frac {
  n: bigint; // pembilang (menyimpan tanda)
  d: bigint; // penyebut (selalu > 0)
}

function absBig(x: bigint): bigint {
  return x < 0n ? -x : x;
}

function gcd(a: bigint, b: bigint): bigint {
  a = absBig(a);
  b = absBig(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1n;
}

export function makeFrac(n: bigint | number, d: bigint | number = 1n): Frac {
  let nn = typeof n === "bigint" ? n : BigInt(Math.round(n));
  let dd = typeof d === "bigint" ? d : BigInt(Math.round(d));
  if (dd === 0n) return { n: 0n, d: 1n };
  if (dd < 0n) {
    nn = -nn;
    dd = -dd;
  }
  const g = gcd(nn, dd);
  return { n: nn / g, d: dd / g };
}

export const ZERO: Frac = { n: 0n, d: 1n };

export function addF(a: Frac, b: Frac): Frac {
  return makeFrac(a.n * b.d + b.n * a.d, a.d * b.d);
}

export function mulF(a: Frac, b: Frac): Frac {
  return makeFrac(a.n * b.n, a.d * b.d);
}

export function cmpF(a: Frac, b: Frac): number {
  const l = a.n * b.d;
  const r = b.n * a.d;
  return l < r ? -1 : l > r ? 1 : 0;
}

export function isZero(a: Frac): boolean {
  return a.n === 0n;
}

// Persentase = fraksi * 100, dibulatkan setengah ke atas pada 2 desimal.
// Dikembalikan sebagai basis-poin bilangan bulat (mis. 3340 = 33,40%).
export function toPercentBasisPoints(f: Frac): bigint {
  // f.n / f.d * 10000, half-up
  const num = f.n * 10000n;
  const den = f.d;
  if (den === 0n) return 0n;
  const q = num / den;
  const r = num % den;
  // pembulatan setengah ke atas (nilai kepemilikan tidak negatif)
  if (r * 2n >= den) return q + 1n;
  return q;
}

// "33,40%" format Indonesia
export function toPercentString(f: Frac): string {
  const bp = toPercentBasisPoints(f); // integer basis points (2 desimal)
  const neg = bp < 0n;
  const abs = neg ? -bp : bp;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const fracStr = frac.toString().padStart(2, "0");
  return `${neg ? "-" : ""}${whole.toString()},${fracStr}%`;
}

// "167/500"
export function toFractionString(f: Frac): string {
  if (f.n === 0n) return "0";
  if (f.d === 1n) return f.n.toString();
  return `${f.n.toString()}/${f.d.toString()}`;
}

// Perbandingan dengan ambang (mis. > 25% atau < 1%)
export function greaterThanPercent(f: Frac, percent: number): boolean {
  // f > percent/100  <=>  f.n * 100 > percent * f.d
  return f.n * 100n > BigInt(percent) * f.d;
}

export function lessThanPercent(f: Frac, percent: number): boolean {
  return f.n * 100n < BigInt(percent) * f.d;
}
